import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  RefObject,
  ReactNode,
} from 'react';
import { Line, Circle } from 'react-konva';
import { isSafari } from 'react-device-detect';
import { Line as LineType, Circle as CircleType } from '@picsart/web-layering/types';
import {
  cacheNode,
  getNodeLayer,
  getNodeStage,
  getNodeAttrs, Filters,
} from '@picsart/web-layering/rendering/konva/selectors';

import { devicePixelRatio } from 'configs/constants';
import useWindowListener from 'hooks/common/useWindowListener';
import useUpdate from 'hooks/common/useUpdate';

import { EraserSettingsEnum } from './config';
import { SettingDataType } from './EraserSettings';
import { toggleScrollBar, getScaledPointer } from './utils';
import { batchDrawNode } from './selectors';

interface IEraserLineProps {
  firstCircleColor: string;
  secondCircleColor: string;
  containerRef: RefObject<HTMLDivElement>;
  settings: SettingDataType;
  onChange: (lineRef: LineType | null, cacheOffset: number) => Promise<void>;
  scale: number;
  onAction: () => void;
  children: ReactNode;
  actionsIsBlock: boolean;
}

const EraserLine = ({
  firstCircleColor,
  secondCircleColor,
  containerRef,
  settings,
  onChange,
  onAction,
  scale,
  children,
  actionsIsBlock,
}: IEraserLineProps) => {
  const lineRef = useRef<LineType>(null);
  const cursorRef = useRef<CircleType>(null);

  const [isPaint, setIsPaint] = useState<boolean>(false);
  const [linePoints, setLinePoints] = useState<number[]>([]);
  const [position, setPosition] = useState<object | null>(null);

  const size = useMemo(
    () => settings[EraserSettingsEnum.SIZE] * (settings[EraserSettingsEnum.HARDNESS] / 100),
    [settings],
  );
  const blurRadius = useMemo(() => (settings[EraserSettingsEnum.SIZE] - size) * 2, [settings, size]);
  const offset = blurRadius / devicePixelRatio;

  const applyCacheToLine = useCallback(() => {
    if (!lineRef.current || offset === 0) {
      return;
    }

    cacheNode(lineRef.current, { offset });
    batchDrawNode(getNodeLayer(lineRef.current));
  }, [offset]);

  const handleMouseDown = useCallback(e => {
    if (e.button === 2) {
      return;
    }

    const stage = lineRef.current && getNodeStage(lineRef.current);
    if (!stage) {
      return;
    }
    setIsPaint(true);

    const { x, y } = getScaledPointer(stage);
    // This is a forced solution to force the safari browser to draw the canvas on touch
    x && y && setLinePoints(isSafari ? [x, y, x, y + 0.001] : [x, y]);

    onAction();
  }, [onAction]);

  const handleMouseMove = useCallback(e => {
    if (e.onScrollBar) {
      setPosition(null);
      return;
    }

    const stage = lineRef.current && getNodeStage(lineRef.current);

    const { x, y } = getScaledPointer(stage);
    x && y && setPosition({
      x,
      y,
    });

    if (!isPaint) {
      return;
    }

    x && y && setLinePoints(prev => [...prev, x, y]);
    applyCacheToLine();
  }, [isPaint, applyCacheToLine]);

  const handleMouseUp = useCallback(async e => {
    if (!isPaint || e.button === 2 || !linePoints.length) {
      return;
    }

    // Don't do anything if eraser/brush width is "0"
    if ((lineRef.current && getNodeAttrs(lineRef.current).strokeWidth === 0)) {
      setIsPaint(false);
      setLinePoints([]);

      return false;
    }
    const stage = lineRef.current && getNodeStage(lineRef.current);
    const { x, y } = getScaledPointer(stage);

    linePoints.length === 2 && x && y && setLinePoints([x, y, x, y]);
    await onChange(lineRef.current, offset);

    setIsPaint(false);
    setLinePoints([]);
  }, [isPaint, linePoints.length, offset, onChange]);

  const handleMouseLeave = useCallback(() => setPosition(null), []);

  const handleWheel = useCallback(() => {
    if (scale > 1) {
      setPosition(null);
    }
  }, [scale]);

  const handleMouseOver = useCallback(() => {
    if (!lineRef.current) {
      return;
    }

    toggleScrollBar(lineRef.current, true);
  }, []);

  const handleMouseOut = useCallback(() => {
    if (!lineRef.current) {
      return;
    }

    toggleScrollBar(lineRef.current, false);
  }, []);

  useWindowListener('mouseup', handleMouseUp, isPaint);

  useEffect(() => {
    const stage = lineRef.current && getNodeStage(lineRef.current);
    stage?.addEventListener('mousedown', handleMouseDown);
    stage?.addEventListener('mousemove', handleMouseMove);
    stage?.addEventListener('mouseup', handleMouseUp);
    stage?.addEventListener('mouseover', handleMouseOver);
    stage?.addEventListener('mouseout', handleMouseOut);
    stage?.addEventListener('mouseleave', handleMouseLeave);
    stage?.addEventListener('touchStart', handleMouseDown);
    stage?.addEventListener('touchMove', handleMouseMove);
    stage?.addEventListener('touchEnd', handleMouseUp);
    stage?.addEventListener('wheel.handleWheel', handleWheel);
    return () => {
      stage?.removeEventListener('mousedown');
      stage?.removeEventListener('mousemove');
      stage?.removeEventListener('mouseup');
      stage?.removeEventListener('mouseover');
      stage?.removeEventListener('mouseout');
      stage?.removeEventListener('mouseleave');
      stage?.removeEventListener('touchStart');
      stage?.removeEventListener('touchMove');
      stage?.removeEventListener('touchEnd');
      stage?.removeEventListener('wheel.handleWheel');
    };
  }, [
    handleMouseLeave, handleMouseDown, handleMouseMove, handleMouseUp, handleWheel, handleMouseOver, handleMouseOut,
  ]);

  useUpdate(() => {
    if (!containerRef || !containerRef.current) {
      return;
    }

    const stage = lineRef.current && getNodeStage(lineRef.current);

    if (!stage) {
      return;
    }

    const {
      scaleX: stageScaleX,
      scaleY: stageScaleY,
      width,
      height,
    } = getNodeAttrs(stage);

    const { clientWidth, clientHeight } = containerRef.current;

    const posX = clientWidth > width ? width : clientWidth;
    const posY = clientHeight > height ? height : clientHeight;

    setPosition({
      x: posX / stageScaleX / 2,
      y: posY / stageScaleY / 2,
    });
    lineRef.current && batchDrawNode(getNodeLayer(lineRef.current));
  }, [settings.size, settings.opacity]);

  return (
    <>
      <Line
        // @ts-ignore
        ref={lineRef}
        points={linePoints}
        stroke="blue"
        lineCap="round"
        lineJoin="round"
        globalCompositeOperation="destination-out"
        strokeWidth={size}
        opacity={settings[EraserSettingsEnum.OPACITY] / 100}
        // @ts-ignore
        filters={[Filters.Blur]}
        blurRadius={blurRadius}
      />
      {children}
      {!actionsIsBlock && position && (
        <>
          <Circle
            // @ts-ignore
            ref={cursorRef}
            id="cursor-circle"
            stroke={firstCircleColor}
            strokeWidth={0.5}
            radius={settings[EraserSettingsEnum.SIZE] / 2}
            {...position}
          />
          <Circle
            // @ts-ignore
            ref={cursorRef}
            id="cursor-circle-second"
            stroke={secondCircleColor}
            strokeWidth={0.5}
            radius={(settings[EraserSettingsEnum.SIZE] / 2) + 1}
            {...position}
          />
        </>
      )}
    </>
  );
};

export default EraserLine;
